import type { QuestionItem, QuestionTheme, QuestionTree } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import { TestSummaryBar } from 'pages/custom_tests/components/custom_test_wizard/components/test_summary_bar/TestSummaryBar';
import { QuestionPreviewModal } from 'pages/custom_tests/components/question_preview/QuestionPreviewModal';
import type { PreviewQuestion } from 'pages/custom_tests/components/question_preview/QuestionPreviewModal';
import { usePreviewStudent } from 'pages/custom_tests/hooks/usePreviewStudent';
import { Badge, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type TestQuestionsViewProps = {
    questionTree: QuestionTree;
    selectedIds: string;
};

const themeKey = (productId: number, themeId: number) => `${productId}-${themeId}`;

type SortColumn = 'name' | 'qid' | 'source' | 'type';

const themeQuestions = (theme: QuestionTheme): QuestionItem[] => (theme.Objectives ?? []).flatMap(objective => objective.Questions ?? []);

const splitSourceString = (value: string): string[] => value
    .split(/\s*(?:\||;|,|•)\s*/g)
    .map(item => item.trim())
    .filter(Boolean);

// Prefer the display name; the raw QuestionType field is a numeric type id and
// must never be shown (or matched) as if it were a name.
const getQuestionType = (question: QuestionItem): string => {

    const source = question as any;
    const options = [
        question.QuestionTypeName,
        source.QuestionTypeName,
        source.TypeName
    ];

    return options.find(value => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';

};

const getQuestionSources = (question: QuestionItem): string[] => {

    const source = question as any;

    const directSources = [
        ...(Array.isArray(source.Sources) ? source.Sources : []),
        ...(typeof source.QuestionSource === 'string' ? splitSourceString(source.QuestionSource) : []),
        ...(typeof source.Source === 'string' ? splitSourceString(source.Source) : []),
        ...(typeof source.SourceName === 'string' ? splitSourceString(source.SourceName) : []),
        ...(typeof source.SourceType === 'string' ? splitSourceString(source.SourceType) : [])
    ]
        .map(item => String(item).trim())
        .filter(Boolean);

    const bookSources = (question.Books ?? [])
        .map(book => `${book.PublisherName ?? ''} ${book.BookName ?? ''}`.trim())
        .filter(Boolean);

    return Array.from(new Set([...
    directSources,
    ...bookSources]));

};

export const TestQuestionsView = (props: TestQuestionsViewProps) => {

    const { questionTree, selectedIds } = props;

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const { cultureId, userId } = useApp();

    const { resolvePreviewStudentId } = usePreviewStudent();

    const [state, setState] = useSuperState({
        expandedThemes: new Set<string>(),
        previewIndex: 0,
        previewQuestions: [] as PreviewQuestion[],
        previewShow: false,
        previewStudentId: 0,
        sortColumn: '' as SortColumn | '',
        sortDir: 'asc' as 'asc' | 'desc'
    });
    const { expandedThemes, previewIndex, previewQuestions, previewShow, previewStudentId, sortColumn, sortDir } = state;

    const selectedSet = new Set(selectedIds.split(',').map(s => s.trim()).filter(Boolean).map(Number));

    // Read-only view: keep only the questions that belong to this test.
    const products = (questionTree.Products ?? []).map(product => ({
        ...product,
        Themes: (product.Themes ?? []).map(theme => ({
            ...theme,
            Objectives: (theme.Objectives ?? []).map(objective => ({
                ...objective,
                Questions: (objective.Questions ?? []).filter(question => selectedSet.has(question.QuestionId) || question.IsChecked)
            })).filter(objective => objective.Questions.length > 0)
        })).filter(theme => theme.Objectives.length > 0)
    })).filter(product => product.Themes.length > 0);

    const toggleExpanded = (key: string) => setState(prev => {

        const next = new Set(prev.expandedThemes);
        if (next.has(key)) next.delete(key); else next.add(key);
        return { expandedThemes: next };

    });

    // Opens the mock's Question Preview modal (read-only here: no Include toggle), which
    // renders a question by its QuestionId in "in-app" mode. The student site's viewer
    // authorizes teacher previews through a real teacher -> student relationship, so a
    // partner student is resolved first (an empty student id fails to load). The list is
    // the objective's sorted question list so Previous/Next walk the rows the table shows.
    const openQuestionPreview = async (questions: QuestionItem[], question: QuestionItem, objectiveName: string, topicName: string) => {

        const resolvedStudentId = await resolvePreviewStudentId();

        if (!resolvedStudentId) {

            createToast(
                formatMessage({ id: 'question_preview_requires_at_least_one_a.229fb122', defaultMessage: 'Question preview requires at least one active student in the current partner.' }),
                'warning',
                6000
            );
            return;

        }

        const nextPreviewQuestions: PreviewQuestion[] = questions.map(item => ({
            Name: item.Name,
            ObjectiveName: objectiveName,
            QuestionId: item.QuestionId,
            Sources: getQuestionSources(item).join(', '),
            TopicName: topicName,
            TypeName: getQuestionType(item)
        }));

        setState({
            previewIndex: Math.max(0, nextPreviewQuestions.findIndex(item => item.QuestionId === question.QuestionId)),
            previewQuestions: nextPreviewQuestions,
            previewShow: true,
            previewStudentId: resolvedStudentId
        });

    };

    // --- Sorting ---
    // One shared sort descriptor applies to every objective's table; clicking a header cycles
    // that column asc -> desc.
    const cycleSort = (column: SortColumn) => setState({
        sortColumn: column,
        sortDir: sortColumn === column && sortDir === 'asc' ? 'desc' : 'asc'
    });

    const sortQuestions = (questions: QuestionItem[]): QuestionItem[] => {

        if (!sortColumn) return questions;

        const direction = sortDir === 'asc' ? 1 : -1;

        const sortValue = (question: QuestionItem): number | string => {

            if (sortColumn === 'qid') return question.QuestionId;
            if (sortColumn === 'name') return question.Name ?? '';
            if (sortColumn === 'type') return getQuestionType(question);
            return getQuestionSources(question).join(', ');

        };

        return [...questions].sort((a, b) => {

            const left = sortValue(a);
            const right = sortValue(b);

            if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;

            return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * direction;

        });

    };

    const renderSortHeader = (column: SortColumn, label: string) => (
        <button
            className={'btn btn-link btn-sm p-0 text-decoration-none text-muted fw-normal'}
            onClick={() => cycleSort(column)}
            type={'button'}
        >
            {label}
            <i className={`ms-1 fa-solid ${sortColumn === column ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`} />
        </button>
    );

    return (
        <div className={'test-questions-view d-flex flex-column gap-3 py-2'}>

            <TestSummaryBar questionTree={questionTree} selectedQuestionIds={selectedSet} />

            {products.map(product => (
                <div className={'d-flex flex-column gap-2'} key={product.ProductId}>
                    <h5 className={'fw-bold mb-1'}>
                        {product.ProductName}
                    </h5>

                    {product.Themes.map(theme => {

                        const key = themeKey(product.ProductId, theme.ThemeId);
                        const expanded = expandedThemes.has(key);
                        const count = themeQuestions(theme).length;

                        return (
                            <div className={'border rounded'} key={key}>
                                <button
                                    className={'btn btn-link text-dark text-decoration-none w-100 d-flex justify-content-between align-items-center px-3 py-2'}
                                    onClick={() => toggleExpanded(key)}
                                    type={'button'}
                                >
                                    <span className={'fw-semibold'}>
                                        {theme.Name}
                                    </span>
                                    <span className={'d-inline-flex align-items-center gap-2'}>
                                        <Badge bg={'dark'}>
                                            {count}
                                        </Badge>
                                        <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`} />
                                    </span>
                                </button>

                                {expanded && (
                                    <div className={'border-top px-3 py-2 d-flex flex-column gap-3'}>
                                        {theme.Objectives.map(objective => {

                                            const sortedQuestions = sortQuestions(objective.Questions ?? []);

                                            return (
                                                <div className={'table-responsive'} key={objective.ObjectiveId}>
                                                    <div className={'fw-semibold small mb-1'}>
                                                        {objective.Name}
                                                    </div>
                                                    <Table borderless hover className={'mb-0 align-middle'} size={'sm'} style={{ minWidth: '52rem', tableLayout: 'fixed', width: '100%' }}>
                                                        <thead>
                                                            <tr className={'small text-muted'}>
                                                                <th style={{ width: '6rem' }}>
                                                                    {renderSortHeader('qid', formatMessage({ id: 'qid.58366cbe', defaultMessage: 'QID' }))}
                                                                </th>
                                                                <th>
                                                                    {renderSortHeader('name', formatMessage({ id: 'question_name.b3f17db0', defaultMessage: 'Question Name' }))}
                                                                </th>
                                                                <th style={{ width: '14rem' }}>
                                                                    {renderSortHeader('type', formatMessage({ id: 'question_type.4b121765', defaultMessage: 'Question Type' }))}
                                                                </th>
                                                                <th style={{ width: '14rem' }}>
                                                                    <span className={'d-inline-flex align-items-center gap-1'}>
                                                                        {renderSortHeader('source', formatMessage({ id: 'source.f63dd3d7', defaultMessage: 'Source' }))}
                                                                        <OverlayTrigger
                                                                            overlay={(
                                                                                <Tooltip>
                                                                                    <FormattedMessage defaultMessage="Where this question appears: Practice Tests, Courses, or Quizzes." id="where_this_question_appears_practice_tes.cdc570a4" />
                                                                                </Tooltip>
                                                                            )}
                                                                            placement={'top'}
                                                                        >
                                                                            <i className={'fa-solid fa-circle-info text-muted'} />
                                                                        </OverlayTrigger>
                                                                    </span>
                                                                </th>
                                                                <th className={'text-end'} style={{ width: '5rem' }}>
                                                                    <FormattedMessage defaultMessage="Preview" id="preview.9fa9aebd" />
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sortedQuestions.map(question => {

                                                                const questionType = getQuestionType(question);
                                                                const sources = getQuestionSources(question);
                                                                const primarySource = sources[0] ?? '';
                                                                const hasMultipleSources = sources.length > 1;
                                                                const sourceDisplay = hasMultipleSources ? `${primarySource} +${sources.length - 1}` : primarySource;

                                                                return (
                                                                    <tr key={question.QuestionId}>
                                                                        <td className={'text-muted small'}>
                                                                            {question.QuestionId}
                                                                        </td>
                                                                        <td className={'text-truncate'}>
                                                                            <span className={'d-inline-block text-truncate w-100'}>
                                                                                {question.Name}
                                                                            </span>
                                                                        </td>
                                                                        <td className={'text-muted small text-truncate'}>
                                                                            <span className={'d-inline-block text-truncate w-100'}>
                                                                                {questionType || '—'}
                                                                            </span>
                                                                        </td>
                                                                        <td className={'small text-truncate'}>
                                                                            {sources.length ?
                                                                                (
                                                                                    <OverlayTrigger
                                                                                        overlay={(
                                                                                            <Tooltip>
                                                                                                <div className={'d-flex flex-column text-start'}>
                                                                                                    {sources.map(sourceItem => (
                                                                                                        <span key={sourceItem}>
                                                                                                            {sourceItem}
                                                                                                        </span>
                                                                                                    ))}
                                                                                                </div>
                                                                                            </Tooltip>
                                                                                        )}
                                                                                        placement={'top'}
                                                                                    >
                                                                                        <span className={`d-inline-block text-truncate w-100${hasMultipleSources ? ' text-decoration-underline' : ''}`}>
                                                                                            {sourceDisplay}
                                                                                        </span>
                                                                                    </OverlayTrigger>
                                                                                )
                                                                                :
                                                                                (
                                                                                    <span className={'text-muted'}>
                                                                                        —
                                                                                    </span>
                                                                                )}
                                                                        </td>
                                                                        <td className={'text-end'}>
                                                                            <button
                                                                                aria-label={formatMessage({ id: 'preview.9fa9aebd', defaultMessage: 'Preview' })}
                                                                                className={'btn btn-link text-decoration-none p-0 text-primary'}
                                                                                onClick={() => openQuestionPreview(sortedQuestions, question, objective.Name, theme.Name)}
                                                                                title={formatMessage({ id: 'preview.9fa9aebd', defaultMessage: 'Preview' })}
                                                                                type={'button'}
                                                                            >
                                                                                <i className={'fa-regular fa-eye'} />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );

                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            );

                                        })}
                                    </div>
                                )}
                            </div>
                        );

                    })}
                </div>
            ))}

            <QuestionPreviewModal
                cultureId={cultureId}
                index={previewIndex}
                onHide={() => setState({ previewShow: false })}
                onNavigate={nextIndex => setState({ previewIndex: nextIndex })}
                questions={previewQuestions}
                show={previewShow}
                studentId={previewStudentId}
                teacherId={userId}
            />

        </div>
    );

};
